/**
 * SessionManager - Centralized session and map data management
 *
 * Encapsulates global state that was previously stored in window.currentSession
 * and window.currentMapData, providing a clean API for session operations.
 */

import { DEFAULT_GAME_DATE } from './calendar.js';

class SessionManager {
  constructor() {
    this.session = null;
    this.mapData = null;
  }

  /**
   * Get the current session
   * @returns {Object|null} Current session object
   */
  getSession() {
    return this.session;
  }

  /**
   * Get the current map data
   * @returns {Object|null} Current map data object
   */
  getMapData() {
    return this.mapData;
  }

  /**
   * Set the current session
   * @param {Object} session - Session object to set
   */
  setSession(session) {
    this.session = session;
    // Keep window.currentSession for backward compatibility during migration
    window.currentSession = session;
  }

  /**
   * Set the current map data
   * @param {Object} mapData - Map data object to set
   */
  setMapData(mapData) {
    this.mapData = mapData;
    // Keep window.currentMapData for backward compatibility during migration
    window.currentMapData = mapData;
  }

  /**
   * Check if a session is currently loaded
   * @returns {boolean} True if session exists
   */
  hasSession() {
    return this.session !== null;
  }

  /**
   * Check if map data is currently loaded
   * @returns {boolean} True if map data exists
   */
  hasMapData() {
    return this.mapData !== null;
  }

  /**
   * Mark the current session as modified
   * @param {boolean} updateButtons - Whether to trigger undo/redo button updates
   */
  markModified(updateButtons = false) {
    if (this.session) {
      this.session.lastModified = Date.now();
      if (updateButtons) {
        // Dispatch event for undo/redo buttons to update
        document.dispatchEvent(new CustomEvent('session-modified'));
      }
    }
  }

  /**
   * Create a new session for a map
   * @param {Object} mapData - Map data from Hexton Hills
   * @param {string} mapName - Name of the map
   * @param {string} mapFilePath - Path to the map file (optional)
   * @param {boolean} isDemo - Whether this is a demo session (optional, default: false)
   * @returns {Object} The created session
   */
  createNewSession(mapData, mapName, mapFilePath = null, isDemo = false) {
    const session = {
      id: isDemo ? 'demo' : 'session-' + Date.now() + '-' + Math.random().toString(36).substring(2, 11),
      isDemo: isDemo,
      formatVersion: 4,
      appVersion: '1.0.0',
      mapName: mapName,
      mapFile: mapFilePath || `campaigns/${mapName}/${mapName}.json`,
      created: Date.now(),
      lastModified: Date.now(),
      showDisplayIds: false,
      calendar: {
        campaignStartDate: DEFAULT_GAME_DATE,
        currentDate: DEFAULT_GAME_DATE,
        daysElapsed: 1,
        events: []
      },
      hexStates: {},
      tokens: [],
      // Event sourcing fields
      history: [],
      currentEventId: 0,
      timeTravelEventId: null,
      undoStack: [],
      redoStack: []
    };

    // Initialize hex states for all tiles (all hidden initially)
    mapData.tiles.forEach(tile => {
      if (tile.pos) {
        const coord = `${tile.pos.x},${tile.pos.y}`;
        session.hexStates[coord] = {
          revealed: false,
          notes: [],
          icons: []
        };
      }
    });

    this.setSession(session);
    this.setMapData(mapData);

    return session;
  }

  /**
   * Load an existing session
   * @param {Object} sessionData - Session data to load
   * @param {Object} mapData - Map data to load
   */
  loadSession(sessionData, mapData) {
    this.setSession(sessionData);
    this.setMapData(mapData);
  }

  /**
   * Clear the current session and map data
   */
  clearSession() {
    this.session = null;
    this.mapData = null;
    window.currentSession = null;
    window.currentMapData = null;
  }

  /**
   * Get calendar from current session
   * @returns {Object|null} Calendar object or null
   */
  getCalendar() {
    return this.session?.calendar || null;
  }

  /**
   * Get hex states from current session
   * @returns {Object} Hex states object (empty if no session)
   */
  getHexStates() {
    return this.session?.hexStates || {};
  }

  /**
   * Get tokens from current session
   * @returns {Array} Tokens array (empty if no session)
   */
  getTokens() {
    return this.session?.tokens || [];
  }

  /**
   * Get selected hex from current session
   * @returns {Object|null} Selected hex data or null
   */
  getSelectedHex() {
    return this.session?.selectedHex || null;
  }

  /**
   * Set selected hex in current session
   * @param {Object|null} hexData - Hex data to select, or null to deselect
   */
  setSelectedHex(hexData) {
    if (this.session) {
      this.session.selectedHex = hexData;
    }
  }

  /**
   * Check if the session is in read-only mode (historical view)
   * @returns {boolean} True if in historical mode
   */
  isReadOnly() {
    return this.session?.isReadOnly || false;
  }

  /**
   * Set read-only mode for the session
   * @param {boolean} isReadOnly - Whether session should be read-only
   */
  setReadOnly(isReadOnly) {
    if (this.session) {
      this.session.isReadOnly = isReadOnly;
    }
  }
}

// Create singleton instance
export const sessionManager = new SessionManager();

// Export class for testing
export { SessionManager };
